import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Nav, Navbar, Button } from 'react-bootstrap';
import * as Icons from 'react-icons/fa';
import Icon from '../components/Icon';

const AdminLayout: React.FC = () => {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
  
  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 992; // Bootstrap lg breakpoint
      setIsMobile(mobile);
      
      // If switching from mobile to desktop, make sure sidebar is expanded
      if (!mobile && isMobile && sidebarCollapsed) {
        setSidebarCollapsed(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile, sidebarCollapsed]);

  return (
    <div className="admin-layout d-flex flex-column vh-100">
      {/* Header */}
      <Navbar bg="dark" variant="dark" expand="lg" className="mb-0">
        <Container fluid className="position-relative">
          <Button 
            variant="outline-light" 
            className="me-2 d-lg-none position-absolute start-0"
            onClick={toggleSidebar}
            style={{ zIndex: 1 }}
          >
            <Icon icon={Icons.FaBars} />
          </Button>
          <Navbar.Brand className="mx-auto text-center w-100">SpeedSafe Admin</Navbar.Brand>
        </Container>
      </Navbar>

      <Container fluid className="flex-grow-1 p-0 overflow-hidden">
        <Row className="g-0 h-100">
          {/* Sidebar */}
          <Col lg={2} className={`sidebar bg-dark text-white ${sidebarCollapsed ? 'collapsed' : ''}`} 
            style={{ 
              height: 'calc(100vh - 56px)',
              position: 'fixed',
              top: '56px',
              left: 0,
              zIndex: 999,
              width: sidebarCollapsed ? '0' : '200px',
              transform: sidebarCollapsed ? 'translateX(-100%)' : 'translateX(0)',
              transition: 'all 0.3s',
              overflow: 'auto',
              visibility: sidebarCollapsed ? 'hidden' : 'visible',
              display: 'flex',
              flexDirection: 'column'
            }}>
            {/* Main Navigation */}
            <Nav className="flex-column py-3 flex-grow-1">
              <NavLink to="/" className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`} end>
                <Icon icon={Icons.FaHome} className="me-2" /> Dashboard
              </NavLink>
              <NavLink to="/products" className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`}>
                <Icon icon={Icons.FaBox} className="me-2" /> Products
              </NavLink>
              <NavLink to="/services" className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`}>
                <Icon icon={Icons.FaTools} className="me-2" /> Services
              </NavLink>
              <NavLink to="/partners" className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`}>
                <Icon icon={Icons.FaHandshake} className="me-2" /> Partners
              </NavLink>
              <NavLink to="/contacts" className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`}>
                <Icon icon={Icons.FaEnvelope} className="me-2" /> Contacts
              </NavLink>
              <NavLink to="/company-info" className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`}>
                <Icon icon={Icons.FaBuilding} className="me-2" /> Company Info
              </NavLink>
              <NavLink to="/content-management" className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`}>
                <Icon icon={Icons.FaEdit} className="me-2" /> Content Management
              </NavLink>
            </Nav>
            
            {/* User Actions at Bottom of Sidebar */}
            <Nav className="flex-column mt-auto border-top border-secondary py-3">
              <NavLink to="/profile" className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`}>
                <Icon icon={Icons.FaUser} className="me-2" /> Profile
              </NavLink>
              <Button 
                variant="outline-light" 
                onClick={handleLogout} 
                className="mx-3 mt-2 mb-3 d-flex align-items-center"
              >
                <Icon icon={Icons.FaSignOutAlt} className="me-2" /> Logout
              </Button>
            </Nav>
          </Col>

          {/* Backdrop for mobile */}
          {!sidebarCollapsed && isMobile && (
            <div 
              className="d-lg-none" 
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: 'rgba(0,0,0,0.5)',
                zIndex: 998
              }}
              onClick={toggleSidebar}
            />
          )}

          {/* Main Content */}
          <Col 
            lg={{ span: 10, offset: 2 }} 
            xs={12} 
            className="main-content p-4 overflow-auto"
            style={{
              marginLeft: sidebarCollapsed && !isMobile ? 0 : (isMobile ? 0 : '16.666667%'),
              transition: 'all 0.3s',
              height: 'calc(100vh - 56px)',
              paddingBottom: '120px' // Add extra padding at bottom for scrolling
            }}
          >
            <Outlet />
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AdminLayout; 